import { type ReactNode } from 'react';
import { cn } from '@zep-us/zus';
import { RefreshIcon } from '@zep-us/zus-icons';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { type AiBuilderMessage, useAiBuilderChatActions } from '../stores';
import { AiBuilderFilePreviewCard } from './AiBuilderFilePreviewCard';
import { AiBuilderMessageCopyButton } from './AiBuilderMessageCopyButton';
import { AiBuilderPdfPagePreviewList } from './AiBuilderPdfPagePreviewList';
type AiBuilderUserMessageData = Extract<AiBuilderMessage, {
  kind: 'user';
}>;
export const AiBuilderUserMessage = ({
  message
}: {
  message: AiBuilderUserMessageData;
}) => {
  const {
    retryUserMessage
  } = useAiBuilderChatActions();
  const copy = useAiBuilderChatCopy();
  const fileAttachment = message.attachment?.type === 'file' ? message.attachment : null;
  return <div className="flex justify-end pb-md pl-md pr-sm laptop-sm:pl-3xl laptop-sm:pr-lg" data-sentry-component="AiBuilderUserMessage" data-sentry-source-file="AiBuilderUserMessage.tsx">
      <div className="flex max-w-full flex-col items-end gap-2xs">
        {fileAttachment?.pdfSelection ? <AiBuilderPdfPagePreviewList copy={copy.attachmentPreview} file={fileAttachment.file} pageCount={fileAttachment.pdfSelection.pageCount} pageRanges={fileAttachment.pdfSelection.pageRanges} size="message" className={cn('max-w-[416px] justify-end', message.status === 'failed' && 'opacity-[0.64]')} /> : fileAttachment && <ol className={cn(message.status === 'failed' && 'opacity-[0.64]')}>
              <li>
                <AiBuilderFilePreviewCard copy={copy.attachmentPreview} file={fileAttachment.file} size="message" />
              </li>
            </ol>}

        {message.content && <UserBubble content={message.content} status={message.status} />}

        <UserMessageStatusAction message={message} onRetry={() => retryUserMessage(message.id)} data-sentry-element="UserMessageStatusAction" data-sentry-source-file="AiBuilderUserMessage.tsx" />
      </div>
    </div>;
};
const UserBubble = ({
  content,
  status
}: {
  content: string;
  status: AiBuilderUserMessageData['status'];
}) => {
  const isFailed = status === 'failed';
  const isPending = status === 'sending' || isFailed;
  return <div className={cn('max-w-[416px] whitespace-pre-wrap break-words rounded-md border border-opacity-assistive px-md py-sm text-left text-body-lg-medium', isFailed ? 'bg-red-50' : 'bg-strong-alternative', isPending ? 'text-alternative' : 'text-default')} data-sentry-component="UserBubble" data-sentry-source-file="AiBuilderUserMessage.tsx">
      <UserMessageLinkedContent content={content} data-sentry-element="UserMessageLinkedContent" data-sentry-source-file="AiBuilderUserMessage.tsx" />
    </div>;
};
const UserMessageStatusAction = ({
  message,
  onRetry
}: {
  message: AiBuilderUserMessageData;
  onRetry: () => void;
}) => {
  const copy = useAiBuilderChatCopy();
  if (message.status === 'sending') {
    return <div className="flex h-[36px] items-center justify-center rounded-full px-sm text-body-sm-semibold text-neutral opacity-[0.54]">
        {copy.userMessage.sending}
      </div>;
  }
  if (message.status === 'failed') {
    if (!message.retry) {
      return <div className="flex h-[36px] items-center justify-center rounded-full px-sm text-body-sm-semibold text-neutral opacity-[0.54]">
          {copy.userMessage.failed}
        </div>;
    }
    return <button type="button" className="flex h-[36px] items-center justify-center gap-3xs rounded-full px-sm text-body-sm-semibold text-neutral transition-colors duration-150 ease-out hover:bg-opacity-black-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-strong focus-visible:ring-offset-1" onClick={onRetry}>
        <RefreshIcon size="2xs" fill="currentColor" aria-hidden="true" />
        {copy.userMessage.retry}
      </button>;
  }
  return <AiBuilderMessageCopyButton align="end" text={message.content} data-sentry-element="AiBuilderMessageCopyButton" data-sentry-component="UserMessageStatusAction" data-sentry-source-file="AiBuilderUserMessage.tsx" />;
};
const UserMessageLinkedContent = ({
  content
}: {
  content: string;
}) => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  for (const match of content.matchAll(URL_PATTERN)) {
    const url = match[0];
    const index = match.index ?? 0;
    if (index > lastIndex) {
      nodes.push(content.slice(lastIndex, index));
    }
    nodes.push(<a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer" className="text-link underline-offset-2 hover:underline">
        {url}
      </a>);
    lastIndex = index + url.length;
  }
  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex));
  }
  return <>{nodes.length > 0 ? nodes : content}</>;
};
const URL_PATTERN = /https?:\/\/[^\s]+/gi;